import WRAP from "WRAP-JS";
import {
  configPointAreaDh,
  configPointAreaExternalDh,
  configPointAreaExternalLayer,
  configPointAreaLayer,
} from "./config/ConfigPointArea";
import { MAP_ID_CUSTOM_POINT_AREA } from "../../../store/MapContentsState";
import { COMMUNICATE_ERROR_MESSAGE_CUSTOMER_EXTERNAL_FILE, COMMUNICATE_ERROR_MESSAGE_CUSTOMER_POINT_AVAILABLE, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER } from "../../../constants/ErrorMessage";
import Color from "color";
import axios from "axios";

class PointArea {
  static NAME = MAP_ID_CUSTOM_POINT_AREA;
  static POINT_AREA_POINT = `${this.NAME}_POINT`;
  static POINT_AREA_AREA = `${this.NAME}_AREA`;
  static POINT_AREA_IMPORT = `${this.NAME}_IMPORT`;
  static CONTENTS_VISIBLE_STATE_DEFAULT = {
    [this.POINT_AREA_POINT]: true,
    [this.POINT_AREA_AREA]: true,
    [this.POINT_AREA_IMPORT]: true
  };

  contentsVisibleState = {...this.constructor.CONTENTS_VISIBLE_STATE_DEFAULT};

  dh;
  dhExternal;
  layer;
  layerLabel;
  layerExternal;
  visible;
  ready = false;
  dhAutoUpdate = true;
  contentsDispatcher;
  errorMessageInfo;
  externalFileState = {};

  constructor() {
    this.layer = new WRAP.Geo.Layer(this.constructor.NAME);
    this.layer.setVisible(false);
    this.layerLabel = new WRAP.Geo.Layer(this.constructor.NAME + "_LABEL");
    this.layerLabel.setVisible(false);
    this.layerExternal = new WRAP.Geo.Layer(this.constructor.NAME + "_EXTERNAL");
    this.layerExternal.setVisible(false);
    this.layerLabel.disableInteraction(true);
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.ready = true;
  }

  initData() {
    return new Promise(resolve => {
      this.dh = new WRAP.Data(configPointAreaDh);
      this.dh.load('data', null, false, ref => {
        if (this.errorMessageInfo) {
          this.contentsDispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo});
          this.errorMessageInfo = null;
        }
        resolve(ref);
      }, _ => {
        this.errorMessageInfo = this.contentsDispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_CUSTOMER_POINT_AVAILABLE,
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
        }});
      });

      this.dhExternal = new WRAP.Data(configPointAreaExternalDh);
      // externalファイルが1件もないとlayerのstatusがincompletedのままになってしまうので空のjsonを初期設定しておく
      this.dhExternal.update('data', {
        "type": 'FeatureCollection',
        "features": [],
      });
    });
  }

  resetData() {
    if (this.visible) {
      if (this.contentsVisibleState[this.constructor.POINT_AREA_POINT]
          || this.contentsVisibleState[this.constructor.POINT_AREA_AREA]) {

          this.dh.load('data', null, true, ref => {
          if (this.errorMessageInfo) {
            this.contentsDispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo});
            this.errorMessageInfo = null;
          }
        }, _ => {
          this.errorMessageInfo = this.contentsDispatcher({type: 'SHOW_ERROR', payload: {
            message: COMMUNICATE_ERROR_MESSAGE_CUSTOMER_POINT_AVAILABLE,
            additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
          }});
        });
      }
    }
  }

  initLayer() {
    this.layer.configure(this.dh, configPointAreaLayer);
    // this.layer.set({
    //   index: this.tileIndex
    // });

    const attributes = {
      filter: (type) => {
        let contents;
        switch(type) {
          case 1:
            contents = this.constructor.POINT_AREA_POINT;
            break;
          case 2:
          case 3:
            contents = this.constructor.POINT_AREA_AREA;
            break;
          default:
            break;
        }
        return this.contentsVisibleState[contents];
      },
      externalLayer: this.layerLabel,
    }
    this.layer.setAttributes(attributes);
    this.layerExternal.configure(this.dhExternal, configPointAreaExternalLayer);
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this.initialize();
    }
    this.setVisible(visible);
  }
  getLayer() {
    return [this.layerExternal, this.layer, this.layerLabel];
  }

  setVisible(visible, contents) {
    if (contents) {
      this.contentsVisibleState[contents] = visible;
    }
    if (!this.ready) return;

    if (contents) {
      switch(contents) {
        case this.constructor.POINT_AREA_POINT:
        case this.constructor.POINT_AREA_AREA:
          this.layer.invalidate();
          this.layerLabel.invalidate();
          break;
        case this.constructor.POINT_AREA_IMPORT:
          if (visible) {
            this.layerExternal.show();
            this._loadData();
          } else {
            this.layerExternal.hide();
          }
          break;
        default:
      }
    } else {
      if(visible) {
        this.layer.show();
        this.layerLabel.show();
        if (this.contentsVisibleState[this.constructor.POINT_AREA_IMPORT]) {
          this.layerExternal.show();
          this._loadData();
        } else {
          this.layerExternal.hide();
        }
      } else {
        this.layer.hide();
        this.layerLabel.hide();
        this.layerExternal.hide();
      }
      if (this.dhAutoUpdate !== visible) {
        this.dh.setAutoUpdate('data', visible);
        this.dhAutoUpdate = visible;
      }
    }
  }

  getContentsVisibleState() {
    return this.contentsVisibleState;
  }

  resetContentsVisibleState() {
    this.contentsVisibleState = {...PointArea.CONTENTS_VISIBLE_STATE_DEFAULT};
  }

  _visibleFilter(feature) {
    return this.contentsVisibleState[ PointArea.makeContentsKey(feature.properties.kind) ];
  }

  static makeContentsKey(kind) {
    return `${PointArea.NAME}_${kind}`;
  }

  setContentsDispatcher(dispatcher) {
    this.contentsDispatcher = dispatcher;
  }

  setExternalFileList(list) {
    this.externalFileList = Array.from(list)
      .sort((a, b) => a.folder_name.localeCompare(b.folder_name) || a.name.localeCompare(b.name))
      .reverse();
    const org = this.externalFileState || {};
    this.externalFileState = this.externalFileList.reduce((a, v) => {
      a[v.file_id] = Object.assign({}, org[v.file_id], {
        refs: v,
        visible: !!org[v.file_id]?.visible,
      });
      return a;
    }, {});

    const attributes = { ...configPointAreaExternalLayer.Attributes };
    attributes.features = this.externalFileList.map(v => {
      const color = new Color(`#${v.color}`);
      return {
        "selector": {
          "type": "key_value",
          "key": "_links_ext_id",
          "value": v.file_id,
        },
        "style": {
          "default": {
            "type": "point",
            "point_color": color.alpha(0.3).toString(),
            "point_size": 10,
            "line_color": color.toString(),
            "fill_color": color.alpha(0.3).toString(),
            "line_width": 1,
            "line_method": 'GC',
          }
        }
      };
    });
    this.layerExternal.setAttributes(attributes);
    this._loadData();
  }

  setVisibleFile(fileId, visible) {
    if (!this.externalFileState[fileId]) {
      this.externalFileState[fileId] = {};
    }
    const fileState = this.externalFileState[fileId];
    fileState.visible = visible;
    if (visible && !fileState.data) {
      this._loadExternalFile(fileState);
    } else {
      this._updateDisplayData();
    }
  }

  getVisibleFile(fileId) {
    return !!this.externalFileState[fileId]?.visible;
  }

  resetVisibleFiles() {
    if (this.externalFileList === undefined) return;
    this.externalFileList.forEach((externalFile) => {
      const fileState = this.externalFileState[externalFile.file_id];
      if (fileState.visible) {
        fileState.visible = false;
      }
    });
    this._updateDisplayData();
  }

  _loadExternalFile(fileState) {
    if (!this.ready) return;
    axios.get(fileState.refs.url)
      .then(response => response.data)
      .then(json => {
        json.features.forEach((feature, uid) => {
          feature.properties['_links_ext_name'] = fileState.refs.name;
          feature.properties['_links_ext_folder'] = fileState.refs.folder_name;
          feature.properties['_links_ext_id'] = fileState.refs.file_id;
          feature.properties['_links_ext_uid'] = `${fileState.refs.file_id}-${uid}`;
        });
        fileState.data = json.features;
        this._updateDisplayData();
      })
      .catch(e => {
        this.contentsDispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_CUSTOMER_EXTERNAL_FILE,
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
        }});
      });
  }

  _updateDisplayData() {
    if (!this.ready) return;
    const geojson = {
      "type": 'FeatureCollection',
      "features": [],
    };
    this.externalFileList.forEach(externalFile => {
      const fileState = this.externalFileState[externalFile.file_id];
      if (fileState.visible) {
        if (fileState.data) {
          geojson.features = geojson.features.concat(fileState.data);
        }
      }
      void this.layerExternal._renderer.displayWindowWithId(fileState.visible, fileState.refs.file_id);
    });
    this.dhExternal.update('data', geojson);
  }

  _loadData() {
    this.externalFileList.forEach(externalFile => {
      const fileState = this.externalFileState[externalFile.file_id];
      if (fileState.visible) {
        if (!fileState.data) {
          this._loadExternalFile(fileState);
        }
      }
    });
  }
}

export default PointArea;
